import React from 'react';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * A local search box and a prev/next pager for a list that comes in pages.
 *
 * <p>Shared by every list that pages in hundreds and filters the loaded page in the
 * browser — the Queued column, the split view, the planner, the phone's job list and
 * the quotes board's not-sent lane — so the controls read the same everywhere and the
 * range line is written once. "1–100 of 1,234" says where you are AND how much there
 * is; after a migration the second is the question.
 *
 * <p>🔑 The total is whatever the caller passes — the server's count — and it does NOT
 * change when the search narrows the page. The search is about finding one thing on
 * this page; the number is about how much there is.
 *
 * Renders only. It owns no state and speaks no HTTP.
 *
 * @param {string} search the current text
 * @param {(text: string) => void} onSearch
 * @param {number} page 0-based
 * @param {number} totalPages
 * @param {number} total the server's count of everything, across pages
 * @param {number} size items per page
 * @param {(page: number) => void} onPage
 * @param {string} [placeholder]
 * @param {string} searchLabel the accessible name of the box — unique per screen
 * @param {string} pagerLabel names what is being paged, for the buttons' accessible names
 * @param {string|null} [error] a sentence to show under the box — the term the server
 *        would refuse, or the server's own refusal if one ever arrives
 * @param {boolean} [showSearch=true]
 * @param {boolean} [alwaysShowPager=false] show the range line even on a single page
 */
export default function PagedSearchControls({
    search, onSearch, page, totalPages, total, size, onPage,
    placeholder = 'Name or rego', searchLabel, pagerLabel, error = null,
    showSearch = true, alwaysShowPager = false, className = '',
}) {
    const first = total === 0 ? 0 : page * size + 1;
    const last  = Math.min((page + 1) * size, total);
    const showPager = alwaysShowPager || totalPages > 1;

    return (
        <div className={className}>
        <div className="flex items-center gap-2">
            {showSearch && (
                <div className="relative flex-1 min-w-0">
                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => onSearch(e.target.value)}
                        placeholder={placeholder}
                        aria-label={searchLabel}
                        aria-invalid={error ? true : undefined}
                        className={`w-full pl-7 pr-2 h-8 border rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                            error ? 'border-red-300' : 'border-gray-200'}`}
                    />
                </div>
            )}
            {showPager && (
                <div className="flex items-center gap-1 text-[11px] text-gray-500 shrink-0">
                    <span data-testid="paged-range">
                        {first.toLocaleString()}–{last.toLocaleString()} of {total.toLocaleString()}
                    </span>
                    <button
                        type="button"
                        aria-label={`Previous page of ${pagerLabel}`}
                        onClick={() => onPage(page - 1)}
                        disabled={page <= 0}
                        className="w-6 h-6 flex items-center justify-center rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <ChevronLeft size={12} />
                    </button>
                    <button
                        type="button"
                        aria-label={`Next page of ${pagerLabel}`}
                        onClick={() => onPage(page + 1)}
                        disabled={page >= totalPages - 1}
                        className="w-6 h-6 flex items-center justify-center rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <ChevronRight size={12} />
                    </button>
                </div>
            )}
        </div>
        {/* Under the box, as it is typed: the term the server would refuse, in the
            server's own words — so the keystroke never costs a round trip. */}
        {error && (
            <p role="alert" className="mt-1 text-[11px] text-red-700">{error}</p>
        )}
        </div>
    );
}
